import { ChartLayout, Inventory, PlotSpec, SeriesRef, Tab } from './layoutTypes';

/** "sensor_accel.x", or "esc_status[1].rpm" for a second instance. */
export const seriesName = (ref: Pick<SeriesRef, 'topic' | 'instance' | 'field'>): string =>
  `${ref.topic}${ref.instance ? `[${ref.instance}]` : ''}.${ref.field}`;

export const sameRef = (a: SeriesRef, b: SeriesRef) =>
  a.topic === b.topic && a.instance === b.instance && a.field === b.field;

/** topic|instance -> its field names, for membership checks. */
export function inventoryIndex(inventory: Inventory): Map<string, Set<string>> {
  const index = new Map<string, Set<string>>();
  inventory.topics.forEach(t => {
    index.set(`${t.name}|${t.instance}`, new Set(t.fields.map(f => f.name)));
  });
  return index;
}

/**
 * Fit a saved layout to one log: series the log lacks are dropped and reported, their
 * plots stay (an empty plot says so). Plots that carry their own data are left alone.
 */
export function applyLayout(
  layout: ChartLayout, inventory: Inventory,
): { layout: ChartLayout; missing: SeriesRef[] } {
  const index = inventoryIndex(inventory);
  const missing: SeriesRef[] = [];
  const tabs: Tab[] = (layout.tabs || []).map(tab => ({
    ...tab,
    plots: (tab.plots || []).map((plot): PlotSpec => {
      if (plot.inline) return plot;
      const kept = (plot.series || []).filter(ref => {
        const ok = index.get(`${ref.topic}|${ref.instance}`)?.has(ref.field) ?? false;
        if (!ok && !missing.some(m => sameRef(m, ref))) missing.push(ref);
        return ok;
      });
      return kept.length === (plot.series || []).length ? plot : { ...plot, series: kept };
    }),
  }));
  return { layout: { version: 1, tabs }, missing };
}

/** Keys in the order Postgres jsonb returns them (by length, then bytes). */
function savableRef(s: SeriesRef): SeriesRef {
  const out: SeriesRef = { field: s.field, topic: s.topic, instance: s.instance } as SeriesRef;
  if (s.color !== undefined) out.color = s.color;
  if (s.label !== undefined) out.label = s.label;
  return out;
}

/** What a profile stores: the person's tabs only, never the built-in ones or inline data. */
export function toSavable(layout: ChartLayout): ChartLayout {
  return {
    version: 1,
    tabs: layout.tabs
      .filter(t => !t.builtin)
      .map(t => ({
        name: t.name,
        columns: t.columns,
        plots: t.plots
          .filter(p => !p.inline)
          .map(p => {
            // Fixed key order: jsonb hands keys back sorted, and the dirty check is a string compare.
            const out: PlotSpec = { id: p.id, series: p.series.map(savableRef) };
            if (p.title) out.title = p.title;
            if (p.kind) out.kind = p.kind;
            if (p.y) {
              out.y = {};
              if (p.y.max !== undefined) out.y.max = p.y.max;
              if (p.y.min !== undefined) out.y.min = p.y.min;
            }
            return out;
          }),
      })),
  };
}
